import { RANKS, type Card, type SpiderGameEvent, type SpiderView } from '@cardengine/shared';
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { dealRiffle } from '../sound.js';
import { useStore } from '../store.js';

/**
 * Spider's three card animations, kept out of the board.
 *
 * All three are the same trick — measure the DOM, fly a copy of the card from
 * where it came from to where it landed, hide the real one until the copy
 * arrives — so they share one piece of state and one clean-up timer rather
 * than three. What differs between them is only what triggers the flight and
 * where it starts, which is what this file is.
 *
 * Positions are measured live rather than computed from the layout constants,
 * so the animations stay correct at any breakpoint, with the log panel open or
 * shut, and after a resize.
 */

/** Matches the `fly` keyframes the Switch table already uses. */
const FLIGHT_MS = 620;

/** Gap between cards, per animation. A deal reads left to right; a run gathers. */
const DEAL_STAGGER_MS = 45;
const OPENING_STAGGER_MS = 16;
const HOME_STAGGER_MS = 28;

export interface Flight {
  key: string;
  card: Card;
  faceDown: boolean;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  width: number;
  delay: number;
  /** Which keyframes to run: cards arriving, or a finished run leaving. */
  kind: 'deal' | 'home';
}

export interface SpiderFlights {
  flights: Flight[];
  /** Slot ids (a card id, or `down-col-row`) held invisible until their copy lands. */
  hiddenSlots: ReadonlySet<string>;
  /** Home slot indices held empty until the run flying into them arrives. */
  hiddenHome: ReadonlySet<number>;
}

const NOTHING: SpiderFlights = {
  flights: [],
  hiddenSlots: new Set(),
  hiddenHome: new Set(),
};

/** A deal's identity, so a re-deal with no moves made still reads as new. */
function signatureOf(view: SpiderView): string {
  return view.tableau.map((c) => `${c.downCount}:${c.up.map((card) => card.id).join()}`).join('|');
}

function centreOf(element: Element): { x: number; y: number; width: number } {
  const rect = element.getBoundingClientRect();
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2, width: rect.width };
}

/**
 * The thirteen cards a finished run was made of.
 *
 * Synthesised from the suit rather than carried on the event, because a
 * completed run is by definition King down to Ace in one suit — there is
 * nothing to look up. The ids are only React keys; these cards are decoration
 * on an `aria-hidden` layer and never touch the engine.
 */
function runCards(suit: Card['suit'], total: number): Card[] {
  return RANKS.map((rank) => ({ id: `home-${total}-${rank}${suit}`, suit, rank }));
}

export function useSpiderFlights(
  view: SpiderView | null,
  events: SpiderGameEvent[],
  boardRef: RefObject<HTMLElement | null>,
): SpiderFlights {
  const sound = useStore((s) => s.sound);
  const [state, setState] = useState<SpiderFlights>(NOTHING);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Mirrors `state.flights`, so an effect can read what is already in the air. */
  const armed = useRef<Flight[]>([]);
  const previous = useRef<{ dealsUsed: number; lengths: number[]; signature: string } | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  /**
   * Arm a set of flights, on top of whatever is already in the air.
   *
   * Adding rather than replacing matters for one case that comes up constantly
   * at one suit: a dealt row that completes a run fires both animations in the
   * same tick. Replacing would strip the nine cards still flying of their
   * copies and pop them into place; this lets the row land and the run gather
   * afterwards.
   *
   * Everything clears on one timer at the very end. Cards whose copy has
   * already landed sit exactly on top of the real card underneath, so holding
   * the reveal back until then costs nothing visible.
   */
  function arm(next: Flight[], hiddenSlots: Set<string>, hiddenHome: Set<number>): void {
    if (next.length === 0) return;

    // The ref is updated here rather than inside the state updater: updaters
    // have to stay pure, and StrictMode calls them twice in development.
    const flights = [...armed.current, ...next];
    armed.current = flights;

    /*
     * One flick per card, at the animation's own spacing — both read off the
     * flights themselves rather than passed in, so the sound cannot drift out
     * of step with the picture if a stagger is ever retuned.
     */
    if (sound) {
      const first = next[0]!.delay;
      const spacing = next.length > 1 ? (next[1]!.delay - first) / 1000 : 0.045;
      dealRiffle(next.length, spacing, first / 1000);
    }

    setState((current) => ({
      flights,
      hiddenSlots: new Set([...current.hiddenSlots, ...hiddenSlots]),
      hiddenHome: new Set([...current.hiddenHome, ...hiddenHome]),
    }));

    const lastDelay = Math.max(...flights.map((f) => f.delay));
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      armed.current = [];
      setState(NOTHING);
    }, FLIGHT_MS + lastDelay + 40);
  }

  /** How long until everything currently in the air has landed. */
  function inFlightTail(): number {
    const flying = armed.current;
    return flying.length === 0 ? 0 : Math.max(...flying.map((f) => f.delay)) + FLIGHT_MS;
  }

  /** Everyone who has asked not to be moved gets the cards, not the journey. */
  function reducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // -------------------------------------------------------------------------
  // Cards arriving: the opening deal, and each row off the stock
  // -------------------------------------------------------------------------

  useLayoutEffect(() => {
    if (!view) return;

    const lengths = view.tableau.map((column) => column.up.length);
    const signature = signatureOf(view);
    const before = previous.current;
    previous.current = { dealsUsed: view.dealsUsed, lengths, signature };

    if (reducedMotion()) return;
    const board = boardRef.current;
    const stock = board?.querySelector('.spider__stock');
    if (!board || !stock) return;
    const from = centreOf(stock);

    /*
     * The opening deal. Detected by signature rather than by move count alone:
     * dealing again without having touched the previous board leaves
     * `moveNumber` at zero both sides, and that redeal should still animate.
     * A board arriving mid-game — a reconnect, or coming back to /table — has
     * moves on it and is deliberately left alone, since nothing was just dealt.
     */
    const isOpeningDeal =
      view.moveNumber === 0 && view.dealsUsed === 0 && signature !== before?.signature;

    if (isOpeningDeal) {
      const slots = openingOrder(view);
      const flights: Flight[] = [];
      slots.forEach((slot, index) => {
        const element = board.querySelector(`[data-slot-id="${CSS.escape(slot.slotId)}"]`);
        if (!element) return;
        const to = centreOf(element);
        flights.push({
          key: slot.slotId,
          card: slot.card ?? { id: slot.slotId, suit: 'S', rank: 'A' },
          faceDown: slot.card === null,
          fromX: from.x,
          fromY: from.y,
          toX: to.x,
          toY: to.y,
          width: to.width,
          delay: index * OPENING_STAGGER_MS,
          kind: 'deal',
        });
      });

      // A fresh deal supersedes anything left over from the last one.
      armed.current = [];
      setState(NOTHING);
      arm(flights, new Set(flights.map((f) => f.key)), new Set());
      return;
    }

    /*
     * A row off the stock. Driven off the view rather than the `stockDealt`
     * event, which says a deal happened but not which card went where. A column
     * whose face-up count did not go up by exactly one is skipped: that is the
     * column where the dealt card completed a run and thirteen cards left
     * again, and the flourish below covers it instead.
     */
    if (!before || view.dealsUsed <= before.dealsUsed) return;

    const flights: Flight[] = [];
    view.tableau.forEach((column, index) => {
      if (lengths[index] !== (before.lengths[index] ?? 0) + 1) return;
      const card = column.up[column.up.length - 1];
      if (!card) return;

      const element = board.querySelector(`[data-slot-id="${CSS.escape(card.id)}"]`);
      if (!element) return;
      const to = centreOf(element);

      flights.push({
        key: card.id,
        card,
        faceDown: false,
        fromX: from.x,
        fromY: from.y,
        toX: to.x,
        toY: to.y,
        width: to.width,
        delay: flights.length * DEAL_STAGGER_MS,
        kind: 'deal',
      });
    });

    arm(flights, new Set(flights.map((f) => f.key)), new Set());
  }, [view, boardRef]);

  // -------------------------------------------------------------------------
  // A finished run leaving the table
  // -------------------------------------------------------------------------

  useLayoutEffect(() => {
    if (!view || events.length === 0) return;
    if (reducedMotion()) return;

    const completions = events.filter((e) => e.type === 'sequenceCompleted');
    if (completions.length === 0) return;

    const board = boardRef.current;
    if (!board) return;

    const flights: Flight[] = [];
    const hiddenHome = new Set<number>();
    // Wait for a dealt row to finish arriving before gathering the run it made.
    const offset = inFlightTail();

    for (const done of completions) {
      const slot = board.querySelector(`[data-home-slot="${done.total - 1}"]`);
      const column = board.querySelector(`[data-column="${done.column}"]`);
      if (!slot || !column) continue;

      const to = centreOf(slot);
      // The run has already gone from the column, so there is nothing left to
      // measure it from. The column's own box is within a card of where it sat,
      // which for a flourish is near enough.
      const box = column.getBoundingClientRect();
      const from = { x: box.left + box.width / 2, y: box.top + box.width * 0.7 };

      hiddenHome.add(done.total - 1);
      // Ace first so the King lands last and finishes on top, the way the run
      // would come off the table in a pair of hands.
      runCards(done.suit, done.total).forEach((card, index) => {
        flights.push({
          key: card.id,
          card,
          faceDown: false,
          fromX: from.x,
          fromY: from.y,
          toX: to.x,
          toY: to.y,
          width: box.width,
          delay: offset + index * HOME_STAGGER_MS,
          kind: 'home',
        });
      });
    }

    arm(flights, new Set(), hiddenHome);
  }, [events, view, boardRef]);

  return state;
}

/**
 * The opening deal, in the order a pair of hands would deal it: across the
 * columns a row at a time, with the last card of each column face up.
 *
 * Derived from the board rather than from the layout constants, so it stays
 * right if the deal shape ever changes.
 */
function openingOrder(view: SpiderView): { slotId: string; card: Card | null }[] {
  const order: { slotId: string; card: Card | null }[] = [];
  const heights = view.tableau.map((column) => column.downCount + column.up.length);
  const deepest = Math.max(0, ...heights);

  for (let row = 0; row < deepest; row++) {
    view.tableau.forEach((column, index) => {
      if (row >= (heights[index] ?? 0)) return;
      if (row < column.downCount) {
        order.push({ slotId: `down-${index}-${row}`, card: null });
        return;
      }
      const card = column.up[row - column.downCount];
      if (card) order.push({ slotId: card.id, card });
    });
  }
  return order;
}
